import { Warning } from "@phosphor-icons/react";

const ORIGIN_NOTE = {
  template: "template default — not SME approved",
  assumed: "assumed — stated, not from the spec",
};

/** Spec rail: ratings, the component list that drives selection, and the notes
 *  that come with the Design Model. Everything here is read from the model. */
export default function SpecRail({ model, selected, onSelect }) {
  if (!model) return null;
  const byId = Object.fromEntries((model.parts || []).map((p) => [p.id, p]));

  return (
    <div className="flex min-w-0 flex-col gap-7 pb-8 pr-1" data-testid="v3d-spec-rail">
      <section>
        <h2 className="mb-2.5 border-b border-[var(--c-border)] pb-1.5 font-mono text-[11px] uppercase tracking-[0.12em] text-[var(--c-muted)]">Ratings</h2>
        <dl className="grid grid-cols-[auto_1fr] gap-x-3.5 gap-y-1.5 text-[13px]">
          {(model.ratings || []).map((r) => (
            <div key={r.label} className="col-span-2 grid grid-cols-[auto_1fr] items-baseline gap-x-3.5">
              <dt className="text-[var(--c-muted)]">{r.label}</dt>
              <dd className="m-0 text-right font-mono text-[12px] tabular-nums text-[var(--c-text)]" title={r.ref || ""}>{r.value}</dd>
            </div>
          ))}
        </dl>
      </section>

      <section>
        <h2 className="mb-2.5 border-b border-[var(--c-border)] pb-1.5 font-mono text-[11px] uppercase tracking-[0.12em] text-[var(--c-muted)]">Components</h2>
        {(model.categories || []).map((cat) => (
          <div key={cat.id} className="mb-3.5">
            <h3 className="mb-1.5 text-[13px] font-semibold tracking-wide text-[var(--c-text)]">{cat.title}</h3>
            <ul className="m-0 flex list-none flex-col gap-1 p-0">
              {cat.parts.map((pid) => {
                const p = byId[pid];
                if (!p) return null;
                const amber = (p.sources || []).some((s) => s.origin === "template" || s.origin === "assumed");
                return (
                  <li key={pid}>
                    <button type="button" data-testid={`v3d-part-btn-${pid}`}
                      aria-pressed={selected === pid}
                      onClick={() => onSelect?.(pid)}
                      className={`grid w-full grid-cols-[1fr_auto] gap-x-2.5 rounded-md border px-2.5 py-2 text-left transition-colors duration-150 ${
                        selected === pid
                          ? "border-[var(--c-accent)] bg-[var(--c-accent)]/15"
                          : "border-[var(--c-border)] bg-[var(--c-surface)] hover:border-[var(--c-muted)]"}`}>
                      <span className="text-[13.5px] font-semibold text-[var(--c-text)]">{p.name}</span>
                      <span className="whitespace-nowrap font-mono text-[10px] font-bold text-[var(--c-accent)]">{p.clause}</span>
                      <span className="col-span-2 flex items-center gap-1.5 text-[12px] text-[var(--c-muted)]">
                        {p.tip}
                        {amber && <Warning size={11} className="text-[var(--c-amber)]" title="uses unapproved template defaults" />}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </section>

      {!!(model.comparison || []).length && (
        <section>
          <h2 className="mb-2.5 border-b border-[var(--c-border)] pb-1.5 font-mono text-[11px] uppercase tracking-[0.12em] text-[var(--c-muted)]">
            {model.comparison_title || "Comparison"}
          </h2>
          <div className="overflow-x-auto rounded-lg border border-[var(--c-border)] bg-[var(--c-surface)]">
            <table className="w-full border-collapse text-[12.5px]">
              <thead>
                <tr className="text-left font-mono text-[9px] uppercase tracking-wider text-[var(--c-muted)]">
                  <th className="border-b border-[var(--c-border)] px-2.5 py-2">Item</th>
                  <th className="border-b border-[var(--c-border)] px-2.5 py-2">As drawn</th>
                  <th className="border-b border-[var(--c-border)] px-2.5 py-2">Per spec</th>
                </tr>
              </thead>
              <tbody>
                {model.comparison.map((r) => (
                  <tr key={r.item} data-testid={`v3d-cmp-${r.item}`}>
                    <td className="border-b border-[var(--c-border)]/60 px-2.5 py-2 font-semibold text-[var(--c-text)]">{r.item}</td>
                    <td className="border-b border-[var(--c-border)]/60 px-2.5 py-2 text-[var(--c-muted)]">{r.drawn}</td>
                    <td className="border-b border-[var(--c-border)]/60 px-2.5 py-2 text-[var(--c-text)]">{r.per_spec}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {!!(model.notes || []).length && (
        <section>
          <h2 className="mb-2.5 border-b border-[var(--c-border)] pb-1.5 font-mono text-[11px] uppercase tracking-[0.12em] text-[var(--c-muted)]">Notes</h2>
          {model.notes.map((nt) => (
            <p key={nt.title} className="mb-2 max-w-[62ch] text-[12.5px] text-[var(--c-muted)]">
              <b className="font-semibold text-[var(--c-text)]">{nt.title}</b> {nt.text}
            </p>
          ))}
          <p className="mt-1 font-mono text-[10px] text-[var(--c-muted)]">
            Value origins — <span className="text-[var(--c-green)]">spec</span>,{" "}
            <span className="text-[var(--c-accent)]">rule</span>,{" "}
            <span className="text-[var(--c-amber)]">template / assumed</span> ({ORIGIN_NOTE.template}).
          </p>
        </section>
      )}
    </div>
  );
}
